import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { getApiError } from '../utils/apiError';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { toast } from 'sonner';
import { Plus, Trash2, FileText, Edit, Save, Code2, User, Building2, Briefcase, Globe, Phone, Linkedin, DollarSign, FileText as FileTextIcon, Mail } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';

// Variable options mapped to Lead Form fields
const VARIABLE_OPTIONS = [
    {
        category: 'Lead Info',
        icon: User,
        variables: [
            { label: 'First Name (Virtual)', value: 'first_name', description: 'Extracted from Full Name', field: 'name' },
            { label: 'Full Name', value: 'full_name', description: 'Lead full name', field: 'name' },
            { label: 'Email', value: 'email', description: 'Lead email address', field: 'email' },
            { label: 'Company', value: 'company', description: 'Company name', field: 'company' },
            { label: 'Job Title', value: 'job_title', description: 'Job title/position', field: 'job_title' },
            { label: 'Website', value: 'website', description: 'Company website', field: 'website' },
            { label: 'Phone Number', value: 'phone_number', description: 'Contact phone', field: 'phone_number' },
            { label: 'LinkedIn Profile', value: 'linkedin_profile', description: 'LinkedIn URL', field: 'linkedin_profile' },
            { label: 'Estimated Value', value: 'estimated_value', description: 'Lead value', field: 'estimated_value' },
            { label: 'Source', value: 'source', description: 'Lead source', field: 'source' },
        ]
    },
    {
        category: 'Sender Info',
        icon: Mail,
        variables: [
            { label: 'My Name', value: 'sender_name', description: 'Your name', field: null },
            { label: 'My Email', value: 'sender_email', description: 'Your email', field: null },
        ]
    }
];

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TemplatesPage = () => {
    const { user } = useAuth();
    const [templates, setTemplates] = useState([]);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [templateForm, setTemplateForm] = useState({
        name: '',
        subject: '',
        body_html: '',
        is_global: true
    });
    const editorRef = useRef(null);
    const subjectInputRef = useRef(null);
    const [subjectAutocompleteOpen, setSubjectAutocompleteOpen] = useState(false);
    const [subjectVariableQuery, setSubjectVariableQuery] = useState('');
    const [subjectAutocompletePosition, setSubjectAutocompletePosition] = useState({ top: 0, left: 0 });

    // Get all variables for filtering
    const getAllVariables = () => {
        return VARIABLE_OPTIONS.flatMap(group => group.variables);
    };

    // If query is empty (just typed {), show all variables
    // Otherwise filter by query
    const filteredSubjectVariables = subjectVariableQuery === ''
        ? getAllVariables()
        : getAllVariables().filter(v => 
            v.value.toLowerCase().includes(subjectVariableQuery.toLowerCase()) ||
            v.label.toLowerCase().includes(subjectVariableQuery.toLowerCase())
        );

    useEffect(() => {
        fetchTemplates();
    }, []);

    const handleSubjectKeyDown = (e) => {
        const input = e.target;
        const cursorPos = input.selectionStart || 0;
        const textBeforeCursor = templateForm.subject.substring(0, cursorPos);
        
        // Check if we're typing { for variable autocomplete
        const variableMatch = textBeforeCursor.match(/\{(\w*)$/);
        
        if (variableMatch) {
            const query = variableMatch[1].toLowerCase();
            setSubjectVariableQuery(query);
            
            // Get input position for popover
            const rect = input.getBoundingClientRect();
            setSubjectAutocompletePosition({ 
                top: rect.bottom, 
                left: rect.left 
            });
            
            setSubjectAutocompleteOpen(true);
        } else {
            setSubjectAutocompleteOpen(false);
        }
        
        // Handle Enter key in autocomplete
        if (subjectAutocompleteOpen && e.key === 'Enter') {
            e.preventDefault();
            if (filteredSubjectVariables.length > 0) {
                insertSubjectVariable(filteredSubjectVariables[0].value);
            }
            return;
        }
        
        // Handle Escape to close autocomplete
        if (subjectAutocompleteOpen && e.key === 'Escape') {
            setSubjectAutocompleteOpen(false);
        }
    };

    const insertSubjectVariable = (variable) => {
        if (!subjectInputRef.current) return;
        
        const input = subjectInputRef.current;
        const start = input.selectionStart || 0;
        const end = input.selectionEnd || 0;
        const varText = `{{${variable}}}`;
        
        // Find the { character position
        const textBeforeCursor = templateForm.subject.substring(0, start);
        const variableMatch = textBeforeCursor.match(/\{(\w*)$/);
        
        if (variableMatch) {
            // Replace {query with full variable
            const matchLength = variableMatch[0].length;
            const deleteFrom = start - matchLength;
            const newValue = 
                templateForm.subject.substring(0, deleteFrom) + 
                varText + 
                templateForm.subject.substring(end);
            setTemplateForm({ ...templateForm, subject: newValue });
            
            // Set cursor position after inserted variable
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(deleteFrom + varText.length, deleteFrom + varText.length);
            }, 0);
        } else {
            // Normal insertion at cursor
            const newValue = 
                templateForm.subject.substring(0, start) + 
                varText + 
                templateForm.subject.substring(end);
            setTemplateForm({ ...templateForm, subject: newValue });
            
            // Set cursor position after inserted variable
            setTimeout(() => {
                input.focus();
                input.setSelectionRange(start + varText.length, start + varText.length);
            }, 0);
        }
        
        setSubjectAutocompleteOpen(false);
    };

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/templates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTemplates(response.data);
        } catch (error) {
            toast.error('Failed to fetch templates');
        }
    };

    const openTemplateDialog = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setTemplateForm({
                name: template.name,
                subject: template.subject,
                body_html: template.body_html,
                is_global: template.is_global
            });
        } else {
            setEditingTemplate(null);
            setTemplateForm({
                name: '',
                subject: '',
                body_html: '',
                is_global: true
            });
        }
        setTemplateDialogOpen(true);
    };

    const saveTemplate = async () => {
        if (!templateForm.name || !templateForm.subject || !templateForm.body_html) {
            toast.error('Please fill all required fields');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (editingTemplate) {
                await axios.put(
                    `${API_URL}/templates/${editingTemplate.id}`,
                    templateForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Template updated successfully');
            } else {
                await axios.post(
                    `${API_URL}/templates`,
                    templateForm,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                toast.success('Template created successfully');
            }
            setTemplateDialogOpen(false);
            fetchTemplates();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to save template'));
        }
    };

    const deleteTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;

        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/templates/${templateId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Template deleted successfully');
            fetchTemplates();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to delete template'));
        }
    };

    return (
        <div className="p-6 md:p-8 lg:p-10 space-y-8">
            <div>
                <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Email Templates</h1>
                <p className="text-sm leading-relaxed text-slate-600">
                    Create and manage email templates with smart variables. Use variables like {`{{first_name}}`}, {`{{company}}`}, and more to personalize your emails.
                </p>
            </div>

            <Card className="rounded-xl border-slate-200 shadow-sm bg-white p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-xl font-medium text-slate-900">Templates</h2>
                        <p className="text-sm text-slate-600 mt-1">Create and manage email templates with smart variables</p>
                    </div>
                    <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                        <DialogTrigger asChild>
                            <Button
                                onClick={() => openTemplateDialog(null)}
                                className="rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create Template
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-5xl rounded-2xl max-h-[90vh] overflow-hidden flex flex-col">
                            <DialogHeader>
                                <DialogTitle className="text-2xl font-semibold text-slate-900">
                                    {editingTemplate ? 'Edit Template' : 'Create Template'}
                                </DialogTitle>
                                <DialogDescription>
                                    {editingTemplate ? 'Update your email template with smart variables' : 'Create a new email template with smart variables'}
                                </DialogDescription>
                            </DialogHeader>
                            <div className="flex-1 flex gap-4 mt-4 overflow-hidden">
                                {/* Main Content */}
                                <div className="flex-1 space-y-4 overflow-y-auto pr-2">
                                    <div>
                                        <Label>Template Name</Label>
                                        <Input
                                            value={templateForm.name}
                                            onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                                            placeholder="e.g., Cold Outreach V2"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div className="relative">
                                        <Label>Subject</Label>
                                        <Input
                                            ref={subjectInputRef}
                                            value={templateForm.subject}
                                            onChange={(e) => {
                                                setTemplateForm({ ...templateForm, subject: e.target.value });
                                                // Check for autocomplete trigger after state update
                                                setTimeout(() => {
                                                    if (subjectInputRef.current) {
                                                        const input = subjectInputRef.current;
                                                        const cursorPos = input.selectionStart || 0;
                                                        const textBeforeCursor = e.target.value.substring(0, cursorPos);
                                                        const variableMatch = textBeforeCursor.match(/\{(\w*)$/);
                                                        
                                                        if (variableMatch) {
                                                            const query = variableMatch[1].toLowerCase();
                                                            setSubjectVariableQuery(query);
                                                            const rect = input.getBoundingClientRect();
                                                            setSubjectAutocompletePosition({ 
                                                                top: rect.bottom, 
                                                                left: rect.left 
                                                            });
                                                            setSubjectAutocompleteOpen(true);
                                                        } else {
                                                            setSubjectAutocompleteOpen(false);
                                                        }
                                                    }
                                                }, 10);
                                            }}
                                            onKeyDown={handleSubjectKeyDown}
                                            placeholder="Email subject (type { to see variable autocomplete)"
                                            className="rounded-lg"
                                        />
                                    </div>
                                    <div>
                                        <Label>Body</Label>
                                        <div 
                                            className="relative"
                                            onMouseDown={(e) => {
                                                // When clicking in the editor area, ensure editor gets focus
                                                if (editorRef.current && editorRef.current.view) {
                                                    setTimeout(() => {
                                                        try {
                                                            editorRef.current.view.focus();
                                                        } catch (err) {
                                                            // Ignore focus errors
                                                        }
                                                    }, 0);
                                                }
                                            }}
                                        >
                                        <RichTextEditor
                                            content={templateForm.body_html}
                                            onChange={(html) => setTemplateForm({ ...templateForm, body_html: html })}
                                            placeholder="Write your template... Type { to see variable autocomplete"
                                            showVariablePicker={false}
                                            onEditorReady={(editor) => {
                                                editorRef.current = editor;
                                            }}
                                        />
                                        </div>
                                    </div>
                                </div>

                                {/* Variable Picker Sidebar */}
                                <div className="w-80 border-l border-slate-200 pl-4 flex flex-col">
                                    <div className="mb-4">
                                        <h3 className="text-sm font-semibold text-slate-900 mb-1 flex items-center gap-2">
                                            <Code2 className="h-4 w-4" />
                                            Variables
                                        </h3>
                                        <p className="text-xs text-slate-500">
                                            Type <code className="bg-slate-100 px-1 rounded">{`{`}</code> in the editor to see autocomplete
                                        </p>
                                    </div>
                                    <ScrollArea className="flex-1">
                                        <div className="space-y-6 pr-2">
                                            {VARIABLE_OPTIONS.map((group) => {
                                                const Icon = group.icon;
                                                return (
                                                    <div key={group.category}>
                                                        <div className="flex items-center gap-2 mb-3">
                                                            <Icon className="h-4 w-4 text-slate-600" />
                                                            <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                                                                {group.category}
                                                            </h4>
                                                        </div>
                                                        <div className="space-y-2">
                                                            {group.variables.map((variable) => (
                                                                <div key={variable.value} className="p-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
                                                                    <div className="flex items-start justify-between gap-2">
                                                                        <div className="flex-1">
                                                                            <div className="flex items-center gap-2 mb-1">
                                                                                <code className="text-xs font-mono bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded">
                                                                                    {`{{${variable.value}}}`}
                                                                                </code>
                                                                                <span className="text-xs font-medium text-slate-900">
                                                                                    {variable.label}
                                                                                </span>
                                                                            </div>
                                                                            {variable.description && (
                                                                                <p className="text-xs text-slate-500 mt-1">
                                                                                    {variable.description}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </ScrollArea>
                                </div>
                            </div>
                            
                            {/* Footer with Global Template toggle and action buttons */}
                            <div className="border-t border-slate-200 pt-4 mt-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex flex-col">
                                        <Label>Global Template</Label>
                                        <p className="text-xs text-slate-500">Available to all team members</p>
                                    </div>
                                    <Switch
                                        checked={templateForm.is_global}
                                        onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_global: checked })}
                                    />
                                </div>
                                <div className="flex gap-2">
                                    <Button
                                        onClick={saveTemplate}
                                        className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                    >
                                        <Save className="h-4 w-4 mr-2" />
                                        {editingTemplate ? 'Update Template' : 'Create Template'}
                                    </Button>
                                    <Button
                                        onClick={() => setTemplateDialogOpen(false)}
                                        variant="outline"
                                        className="rounded-lg font-medium transition-all active:scale-95"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {templates.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                        <FileText className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                        <p>No templates yet</p>
                        <p className="text-sm">Create your first template to get started</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {templates.map((template) => (
                            <div
                                key={template.id}
                                className="p-4 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-between"
                            >
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FileText className="h-5 w-5 text-slate-600" />
                                        <p className="font-medium text-slate-900">{template.name}</p>
                                        {template.is_global && (
                                            <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-md">Global</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-slate-600 mb-1">{template.subject}</p>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {template.variables && template.variables.length > 0 && (
                                            <>
                                                <span className="text-xs text-slate-500">Variables:</span>
                                                {template.variables.map((varName) => (
                                                    <span
                                                        key={varName}
                                                        className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded"
                                                    >
                                                        {`{{${varName}}}`}
                                                    </span>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-400 mt-1">
                                        Created {format(new Date(template.created_at), 'PPp')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => openTemplateDialog(template)}
                                        variant="ghost"
                                        size="sm"
                                        className="text-slate-600 hover:text-slate-900"
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    {(!template.is_global || template.owner_id === user?.id) && (
                                        <Button
                                            onClick={() => deleteTemplate(template.id)}
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Subject Variable Autocomplete Popover - Using Portal */}
            {subjectAutocompleteOpen && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed z-[100] w-80 rounded-md border bg-white shadow-lg"
                    style={{
                        top: `${subjectAutocompletePosition.top}px`,
                        left: `${subjectAutocompletePosition.left}px`,
                        position: 'fixed',
                    }}
                >
                    <Command>
                        <CommandInput placeholder="Search variables..." value={subjectVariableQuery} />
                        <CommandList>
                            <CommandEmpty>No variables found.</CommandEmpty>
                            {VARIABLE_OPTIONS.map((group) => (
                                <CommandGroup key={group.category} heading={group.category}>
                                    {filteredSubjectVariables
                                        .filter(v => group.variables.some(gv => gv.value === v.value))
                                        .map((variable) => (
                                            <CommandItem
                                                key={variable.value}
                                                onSelect={() => insertSubjectVariable(variable.value)}
                                                className="cursor-pointer"
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{variable.label}</span>
                                                    <span className="text-xs text-slate-500 font-mono">{`{{${variable.value}}}`}</span>
                                                </div>
                                            </CommandItem>
                                        ))}
                                </CommandGroup>
                            ))}
                        </CommandList>
                    </Command>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TemplatesPage;
